import { inject, Injectable, Type } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ICustomCtaValue, IGlobalCtaValue } from '@campaign-form/campaign-form.interface';
import { EAnalyticsStatus } from '@enums/campaign-analytics.enum';
import { TranslateService } from '@ngx-translate/core';
import { v4 as uuidv4 } from 'uuid';
import { AudioTrackingComponent } from '../../audio-tracking/audio-tracking.component';
import { CaptionComponent } from '../../caption/caption.component';
import { ContentComponent } from '../../content/content.component';
import { IDeliverableConfig, TDeliverable } from '../../deliverable-list.interface';
import { DescriptionComponent } from '../../description/description.component';
import { HashtagComponent } from '../../hashtag/hashtag.component';
import { MentionComponent } from '../../mention/mention.component';
import { MessageComponent } from '../../message/message.component';
import { TalkingPointComponent } from '../../talking-point/talking-point.component';
import { TextTrackingComponent } from '../../text-tracking/text-tracking.component';
import { TitleComponent } from '../../title/title.component';
import {
  ETextDeliverableMatchMode,
  ITextContentForm,
  ITextDeliverable,
  ITextDeliverableForm,
  TTextDeliverableType,
} from './text-deliverable.interface';

export interface ITextDeliverableServiceConfig {
  component: Type<
    | TitleComponent
    | TalkingPointComponent
    | TextTrackingComponent
    | AudioTrackingComponent
    | CaptionComponent
    | HashtagComponent
    | MentionComponent
    | MessageComponent
    | DescriptionComponent
    | ContentComponent
  >;
  deliverableType: TTextDeliverableType;
  defaultNameTranslationKey: string;
}

@Injectable({
  providedIn: 'root',
})
export class TextDeliverableService {
  private readonly translate = inject(TranslateService);

  public getConfig(config: ITextDeliverableServiceConfig): IDeliverableConfig {
    return {
      component: config.component,
      createForm: (
        item?: TDeliverable,
        creatorId?: string,
        isViewMode?: boolean,
      ): FormGroup<ITextDeliverableForm> | null => {
        if (item && item.type !== config.deliverableType) {
          return null;
        }

        const itemTyped = item as ITextDeliverable | undefined;

        const form = new FormGroup({
          contents: new FormArray<FormGroup<ITextContentForm>>(
            (itemTyped?.contents ?? [{ id: uuidv4(), text: '' }]).map(
              (content) =>
                new FormGroup<ITextContentForm>({
                  analyticsCurrentCount: new FormControl<number | null>(content.analyticsCurrentCount ?? null),
                  customCtaValues: new FormControl<ICustomCtaValue[]>(content?.customCtaValues ?? [], {
                    nonNullable: true,
                  }),
                  exclusiveTo: new FormControl<string | null>(content.exclusiveTo || creatorId || null),
                  globalCtaValues: new FormControl<IGlobalCtaValue[] | null>(content.globalCtaValues ?? null),
                  id: new FormControl<string | null>(content.id ?? uuidv4()),
                  status: new FormControl<EAnalyticsStatus | null>(content.status ?? null),
                  text: new FormControl<string | null>(
                    content.text,
                    isViewMode ? [] : [Validators.required, Validators.maxLength(512)],
                  ),
                }),
            ),
          ),
          customCtaValues: new FormControl<ICustomCtaValue[]>(itemTyped?.customCtaValues ?? []),
          exclusiveTo: new FormControl<string | null>(itemTyped?.exclusiveTo || creatorId || null),
          globalCtaValues: new FormControl<IGlobalCtaValue[] | null>(itemTyped?.globalCtaValues ?? null),
          id: new FormControl<string | null>(itemTyped?.id ?? uuidv4()),
          isNew: new FormControl<boolean>(!itemTyped?.id),
          matchMode: new FormControl<ETextDeliverableMatchMode>(itemTyped?.matchMode ?? ETextDeliverableMatchMode.and, {
            nonNullable: true,
          }),
          name: new FormControl<string | null>(
            itemTyped?.name ?? this.translate.instant(config.defaultNameTranslationKey),
            [Validators.required, Validators.maxLength(255)],
          ),
          sponsoredContentRelevant: new FormControl<boolean>(itemTyped?.sponsoredContentRelevant ?? true, {
            nonNullable: true,
          }),
          type: new FormControl<TTextDeliverableType>(config.deliverableType, { nonNullable: true }),
        }) as FormGroup<ITextDeliverableForm>;

        if (isViewMode) {
          form.disable();
        }

        return form;
      },
    };
  }
}
